import { createLogger } from '@root/config/src/lib/utils/logger';

export interface EcomailAdapterRegisterConfig {
	/** Ecomail account ID */
	appId: string;
}

/**
 * Ecomail tracking adapter
 * @author filipditrich <ditrich@nfctron.com>
 * @export
 */
export class EcomailAdapter {
	private allowTracking: boolean = false;
	private isInitialized: boolean = false;
	private logger = createLogger('tracking', ['EcomailAdapter']);

	get ecotrack(): EcomailTracker | undefined {
		if (typeof window === 'undefined') return undefined;
		return (window as any)?.ecotrack as EcomailTracker | undefined;
	}

	constructor(allowTracking: boolean = false) {
		this.allowTracking = allowTracking;
	}

	/**
	 * Updates the tracking consent
	 * @param consent Tracking consent
	 */
	public updateConsent(consent: boolean) {
		this.logger.debug(`Consent state updated to: ${consent ? '✔︎ GRANTED' : '✘ DENIED'}`);
		this.allowTracking = consent;
	}

	/**
	 * Registers Ecomail tracking
	 * @param appId Ecomail account ID
	 */
	public register(config: EcomailAdapterRegisterConfig, onLoad?: (it: this) => void) {
		/** ignore when tracking is disallowed */
		if (!this.allowTracking) return;
		this.logger.debug('register(%s) called', config.appId);

		if (!this.isInitialized) {
			this.logger.debug('register(%s) initializing Ecomail tracking', config.appId);
			this.loadLibrary(window, 'ecotrack', onLoad);
		}

		this.ecotrack?.('newTracker', 'cf', 'd2dpiwfhf3tz0r.cloudfront.net', { appId: config.appId });
		this.ecotrack?.('setUserIdFromLocation', 'ecmid');
		this.ecotrack?.('trackPageView');
	}

	/**
	 * Loads the Ecomail tracking library into the window object
	 * @param w Global window object
	 * @param t Ecomail tracker object name
	 */
	/**
	 * Loads the Ecomail tracking library into the window object
	 * @param w Global window object
	 * @param t Ecomail tracker object name
	 */
	private loadLibrary = (w: Window, t: string = 'ecotrack', onLoad?: (it: this) => void) => {
		const onLoadFn = () => {
			this.logger.debug('ecmtr-2.4.2.js tracking library loaded');
			onLoad?.(this);
		};
		/* eslint-disable */
		(function(p: any, l: any, o: any, w: any, i: any) {
			if (!p[i]) {
				/** initialize namespace if not exists */
				p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace || [];
				p.GlobalSnowplowNamespace.push(i);
				
				/** create tracker function */
				p[i] = function() {
					(p[i].q = p[i].q || []).push(arguments);
				};
				
				/** initialize queue */
				p[i].q = p[i].q || [];
				
				/** create script element */
				let n = l.createElement(o);
				let g = l.getElementsByTagName(o)[0];
				
				/** set async and source */
				n.async = 1;
				n.src = w;
				n.onload = onLoadFn;
				
				/** insert script into DOM */
				if (g && g.parentNode && n) {
					g.parentNode.insertBefore(n, g);
				}
			}
		})(w, document, "script", "https://d70shl7vidtft.cloudfront.net/ecmtr-2.4.2.js", t);
		/* eslint-enable */

		this.isInitialized = true;
	};

	/**
	 * Page view handler function
	 */
	public handlePageView = () => {
		if (!this.allowTracking) return;
		this.logger.debug('handlePageView() called');
		this.ecotrack?.('trackPageView');
	};
}

/**
 * Ecomail tracker interface
 */
interface EcomailTracker {
	(event: 'newTracker', name: string, endpoint: string, options: { appId: string }): void;
	(event: 'setUserIdFromLocation', param: string): void;
	(event: 'trackPageView'): void;
}
